import React from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import ManualPowerPriceSettingsForm from './ManualPowerPriceSettingsForm'
import { PowerPriceStateButtonGroup } from '../PowerPriceStateButtonGroup'
import { PowerPriceState } from 'src/Libs/dynamic-tariff-settings/dto'
import StaticPowerPriceSettingsForm from './StaticPowerPriceSettingsForm'
import DynamicPowerPriceSettingsForm from './DynamicPowerPriceSettingsForm'
import { DynamicTariffSettingsFormFields, beginningOfDay, createEmptyWeekdaySelection, endOfDay } from 'src/Libs/dynamic-tariff-settings/constants'
import { Stack } from '@mui/system'
import { Button } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SettingsBox } from 'src/Elements/Data'
import { useNavigate, useParams } from 'react-router-dom'
import { hasAccessToDirectMarketing } from 'src/Libs/installation-extensions'
import { useUser } from 'src/Hooks/useUser'
import { useTypedSelector } from 'src/State/RootReducer'
import { useFetchDynamicTariffSettings } from 'src/Libs/dynamic-tariff-settings/hooks'
import VatPercentField from './VatPercentField'

interface Props {
  hasWritingRights: boolean
}

/**
 * Form to show power prices of dynamic tariff settings form
 */
const PowerPricesSettingsForm: React.FC<Props> = ({ hasWritingRights }) => {
  const { control } = useFormContext()
  const { t } = useCustomTranslation()
  const powerPriceState = useWatch({ control, name: DynamicTariffSettingsFormFields.PowerPriceState })
  const { fields, append, remove } = useFieldArray({
    name: DynamicTariffSettingsFormFields.PowerPrices,
    control,
  })
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const currentInstallation = useTypedSelector(state => state.storage.installation)
  const { userData } = useUser()
  const canAccessExtensions = hasAccessToDirectMarketing(currentInstallation, userData) && powerPriceState === PowerPriceState.Dynamic

  const { dynamicTariffSettings } = useFetchDynamicTariffSettings(installationId)
  const hasExistingManualConfig = dynamicTariffSettings?.powerPriceState === PowerPriceState.Manual

  const navigate = useNavigate()

  const handleAddPeriod = (): void => {
    append({ weekdays: createEmptyWeekdaySelection(), from: beginningOfDay, to: endOfDay, price: 0, description: '' })
  }

  const handleRedirect = (): void => {
    navigate(`/installation-extensions/${installationId}/${storageId}`)
  }

  return (
    <Stack sx={{ borderRadius: 2, mb: 2 }}>
      <PowerPriceStateButtonGroup disabled={!hasWritingRights} hasExistingManualConfig={hasExistingManualConfig} />
      {canAccessExtensions && (
        <SettingsBox
          disabled={!hasWritingRights}
          title={t('dynamic-tariff-settings.automatic-extension.title')}
          description={t('dynamic-tariff-settings.automatic-extension.description')}
          suffix={
            <Button variant="text" onClick={handleRedirect} disabled={!installationId || !storageId || !hasWritingRights}>
              {t('dynamic-tariff-settings.automatic-extension.redirect-button-text')}
            </Button>
          }
        />
      )}
      <VatPercentField hasWritingRights={hasWritingRights} />
      {powerPriceState === PowerPriceState.Manual && (
        <ManualPowerPriceSettingsForm fields={fields} remove={remove} handleAddPeriod={handleAddPeriod} hasWritingRights={hasWritingRights} />
      )}
      {powerPriceState === PowerPriceState.Static && <StaticPowerPriceSettingsForm hasWritingRights={hasWritingRights} />}
      {powerPriceState === PowerPriceState.Dynamic && <DynamicPowerPriceSettingsForm hasWritingRights={hasWritingRights} />}
    </Stack>
  )
}

export default PowerPricesSettingsForm
